import { useCallback, useMemo } from 'react';

import { useProviderAccountsController_getAccounts } from '@/services/hooks';

import { mapAccounts } from './accountMappers';
import type { ProviderAccount } from './accountTypes';
import { type ProviderConfigError, parsedErrorOrNull } from './errors';

export interface UseProviderAccountsResult {
    accounts: ProviderAccount[];
    isLoading: boolean;
    error: ProviderConfigError | null;
    refetch: () => Promise<ProviderAccount[]>;
}

export interface UseProviderAccountsOptions {
    /**
     * False keeps the request from going out. The endpoint answers 403 to anyone but an
     * OptoScale admin, and each refusal would be retried.
     */
    enabled?: boolean;
}

/**
 * Every cage-less provider row, plus the accounts the portal can create. They belong to
 * no client (S1), so the list is the same on every client's page.
 */
export function useProviderAccounts(
    options: UseProviderAccountsOptions = {}
): UseProviderAccountsResult {
    const query = useProviderAccountsController_getAccounts({ enabled: options.enabled ?? true });
    const dtos = query.data?.data;
    const accounts = useMemo(() => (dtos ? mapAccounts(dtos) : []), [dtos]);
    const error = useMemo(() => parsedErrorOrNull(query.error), [query.error]);
    const { refetch: refetchQuery } = query;

    const refetch = useCallback(async () => {
        const result = await refetchQuery();
        const fresh = result.data?.data;
        return fresh ? mapAccounts(fresh) : [];
    }, [refetchQuery]);

    // A disabled query stays in react-query's pending status; it is not loading anything.
    return { accounts, isLoading: query.isLoading, error, refetch };
}
